import React from "react";
import { AlertTriangle } from "lucide-react";
import { ReminderData } from "src/helpers/reminderHelpers";
import Modal from "src/components/common/Modal";
import Button from "../Button";

interface UpdateReminderConfirmationModalProps {
  show: boolean;
  handleClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  reminder: ReminderData | null;
  isLoading?: boolean;
}

const UpdateReminderConfirmationModal: React.FC<
  UpdateReminderConfirmationModalProps
> = ({
  show,
  handleClose,
  onConfirm,
  onCancel,
  reminder,
  isLoading = false,
}) => {
  const getActivityTypeDisplayName = (activityType: string) => {
    const displayNames: Record<string, string> = {
      WATER: "Water",
      SLEEP: "Sleep",
      MEDICATION: "Medication",
      DIET: "Diet",
      EXERCISE: "Exercise",
      SYMPTOMS: "Symptoms",
    };
    return displayNames[activityType] || activityType;
  };

  const getFrequencyDisplayName = (frequency: string) => {
    const displayNames: Record<string, string> = {
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      hourly: "Hourly",
      once: "Once",
    };
    return displayNames[frequency] || frequency;
  };

  return (
    <Modal
      show={show}
      showCloseButton
      handleClose={handleClose}
      title="Update Reminder"
    >
      <div className="relative space-y-4">
        {/* Header with warning icon */}
        {/* <div className="flex items-center justify-center mb-4">
          <div className="p-2 bg-orange-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
        </div> */}

        {/* Reminder details */}
        {reminder && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Activity:
                </span>
                <span className="text-sm text-gray-900">
                  {getActivityTypeDisplayName(reminder.activityType)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Frequency:
                </span>
                <span className="text-sm text-gray-900">
                  {getFrequencyDisplayName(reminder.frequency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Times:
                </span>
                <span className="text-sm text-gray-900">
                  {reminder.customFreqAttributes.defaultTimes.join(", ")}
                </span>
              </div>
              {/* <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Status:
                </span>
                <span
                  className={`text-sm font-medium ${
                    reminder.isActive ? "text-green-600" : "text-gray-500"
                  }`}
                >
                  {reminder.isActive ? "Active" : "Inactive"}
                </span>
              </div> */}
            </div>
          </div>
        )}

        {/* Message */}
        <div className="mb-6">
          <p className="text-gray-700">
            You have an existing reminder for this activity, would you like to
            update it?
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between gap-2">
          <div className="w-full">
            <Button
              variant="outlined"
              rounded="full"
              fullWidth
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              variant="contained"
              rounded="full"
              fullWidth
              onClick={onConfirm}
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateReminderConfirmationModal;
